// src/components/HeroSection.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Link,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { FaFacebook, FaEnvelope, FaGlobe } from 'react-icons/fa';

const words = ['Software', 'Web', 'Mobile', 'AI-Powered'];

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.3 + i * 0.2, type: 'spring', stiffness: 100 },
  }),
};

export default function HeroSection() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  // typewriter
  const [displayText, setDisplayText] = useState('');
  const [wordIndex, setWordIndex]     = useState(0);
  const [typing, setTyping]           = useState(true);

  // parallax
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const parallaxX = useTransform(x, (v) => v * 20);
  const parallaxY = useTransform(y, (v) => v * 20);

  const handleMouse = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    x.set((px - 0.5) * 2);
    y.set((py - 0.5) * 2);
  };

  // typewriter effect
  useEffect(() => {
    const current = words[wordIndex % words.length];
    let timeout;
    if (typing) {
      if (displayText.length < current.length) {
        timeout = setTimeout(
          () => setDisplayText(current.slice(0, displayText.length + 1)),
          100
        );
      } else {
        timeout = setTimeout(() => setTyping(false), 1000);
      }
    } else {
      if (displayText.length > 0) {
        timeout = setTimeout(
          () => setDisplayText(current.slice(0, displayText.length - 1)),
          50
        );
      } else {
        setTyping(true);
        setWordIndex((i) => i + 1);
      }
    }
    return () => clearTimeout(timeout);
  }, [displayText, typing, wordIndex]);

  // iOS-style button sx
  const iosBtnSx = {
    textTransform: 'none',
    borderRadius: '50px',
    px: 4,
    py: 1.5,
    fontWeight: 600,
    backdropFilter: 'blur(12px)',
    bgcolor: alpha(theme.palette.common.white, 0.2),
    color: 'common.white',
    boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.15)}`,
    transition: 'transform 0.2s, background-color 0.2s',
    '&:hover': {
      transform: 'scale(1.03)',
      bgcolor: alpha(theme.palette.common.white, 0.3),
    },
  };

  return (
    <Box
      onMouseMove={handleMouse}
      component={motion.section}
      initial="hidden"
      animate="show"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 50%, ${theme.palette.secondary.main} 100%)`,
        color: 'common.white',
        minHeight: { xs: '70vh', md: '80vh' },
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {/* Parallax blobs */}
      <Box
        component={motion.div}
        style={{ x: parallaxX, y: parallaxY }}
        sx={{
          position: 'absolute',
          top: '15%',
          left: '-10%',
          width: 300,
          height: 300,
          bgcolor: 'info.light',
          opacity: 0.15,
          borderRadius: '50%',
          filter: 'blur(80px)',
        }}
      />
      <Box
        component={motion.div}
        style={{ x: parallaxX, y: parallaxY }}
        sx={{
          position: 'absolute',
          bottom: '-5%',
          right: '-10%',
          width: 400,
          height: 400,
          bgcolor: 'secondary.dark',
          opacity: 0.12,
          borderRadius: '50%',
          filter: 'blur(100px)',
        }}
      />

      <Container sx={{ position: 'relative', zIndex: 2 }}>
        <motion.div variants={containerVariants} custom={0}>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2.25rem', md: '4rem' },
              lineHeight: 1.1,
              mb: 2,
            }}
          >
            We Build{' '}
            <Box
              component="span"
              sx={{
                color: 'info.light',
                borderBottom: '3px solid',
                pb: 1,
              }}
            >
              {displayText}
            </Box>
          </Typography>
        </motion.div>

        <motion.div variants={containerVariants} custom={1}>
          <Typography
            variant="h5"
            sx={{
              mb: 4,
              maxWidth: isMdUp ? 600 : '100%',
              color: 'rgba(255,255,255,0.85)',
            }}
          >
            Next-Gen software, web & mobile solutions for forward-thinking
            businesses — powered by innovation & AI.
          </Typography>
        </motion.div>

        <Stack
          component={motion.div}
          variants={containerVariants}
          custom={2}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mb: 4 }}
        >
          {[
            { href: '/contact', label: 'Get a Quote' },
            { href: '/services', label: 'Our Services' },
            { href: '/webgen', label: 'Free Website' },
            { href: '/photoedit', label: 'Image Editor' },
            { href: '/drop', label: 'Dropshiping Tools' },
          ].map((btn) => (
            <motion.div
              key={btn.href}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <Button
                component={Link}
                href={btn.href}
                size="large"
                sx={iosBtnSx}
              >
                {btn.label}
              </Button>
            </motion.div>
          ))}
        </Stack>

        <motion.div variants={containerVariants} custom={3}>
          <Stack direction="row" spacing={3}>
            {[FaFacebook, FaEnvelope, FaGlobe].map((Icon, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.2, color: theme.palette.info.light }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <Link
                  href={
                    Icon === FaFacebook
                      ? 'https://facebook.com/Lemda.webdevelopment'
                      : Icon === FaEnvelope
                      ? 'mailto:lemda.webdev@gmail.com'
                      : 'https://lk.linkedin.com/company/lemda-pvt-ltd'
                  }
                  target="_blank"
                  rel="noopener"
                  sx={{ color: 'inherit' }}
                >
                  <Icon size={28} style={{ verticalAlign: 'middle' }} />
                </Link>
              </motion.div>
            ))}
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
}
